import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
	MatDialogActions,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
	MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { isResetToPhaseOneDecision, ResetToPhaseOneDecision } from '../../../../api/api-types/reset-to-phase-one.api';
import { ASSIGNMENT_API } from '../../../../api/services/assignment.service';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';

export interface ResetToPhaseOneDialogData {
	clientId: number;
	assignmentId: number;
	blockedDecision?: ResetToPhaseOneDecision;
}

@Component({
	selector: 'lvr-reset-to-phase-one-dialog',
	templateUrl: './reset-to-phase-one-dialog.html',
	styleUrl: './reset-to-phase-one-dialog.scss',
	imports: [MatDialogTitle, MatDialogContent, MatDialogActions, MatProgressSpinner, TranslocoPipe, LvrButton],
})
export class ResetToPhaseOneDialog implements OnInit {
	private readonly api = inject(ASSIGNMENT_API);
	private readonly dialogRef = inject(MatDialogRef<ResetToPhaseOneDialog>);
	private readonly data = inject<ResetToPhaseOneDialogData>(MAT_DIALOG_DATA);
	private readonly transloco = inject(TranslocoService);
	private readonly destroyRef = inject(DestroyRef);
	public readonly status = signal<'checking' | 'ready' | 'blocked' | 'error'>(
		this.data.blockedDecision ? 'blocked' : 'checking',
	);
	public readonly reasons = signal(this.data.blockedDecision?.reasons ?? []);

	public ngOnInit(): void {
		if (!this.data.blockedDecision) void this.check();
	}

	private async check(): Promise<void> {
		this.status.set('checking');
		this.reasons.set([]);
		try {
			const decision = await this.api.getResetToPhaseOneDecision(this.data.clientId, this.data.assignmentId);
			if (this.destroyRef.destroyed) return;
			if (!isResetToPhaseOneDecision(decision)) throw new Error('Invalid reset decision');
			this.reasons.set(decision.reasons);
			this.status.set(decision.canReset && decision.reasons.length === 0 ? 'ready' : 'blocked');
		} catch {
			if (this.destroyRef.destroyed) return;
			this.status.set('error');
		}
	}

	public reasonText(code: string, message: string): string {
		const key = `assignment.reset_to_phase_one.reasons.${code}`;
		const translation = this.transloco.translate(key);
		return translation === key ? message : translation;
	}

	public confirm(): void {
		if (this.status() !== 'ready') return;
		this.dialogRef.close(true);
	}

	public cancel(): void {
		this.dialogRef.close(false);
	}
}
